// The WYSIWYG renderer: composites the plate + text blocks onto a canvas at
// the plate's native resolution, mirroring the editor preview's semantics
// (same fonts, same inline markup, same pill/gradient rules). What the user
// saw is what gets uploaded — no server-side re-render, no font drift, and
// no generative step anywhere in the deterministic path.
import { parseInlineMarkup, renderableLines } from './inlineMarkup';
import { OverlayElement } from './types';

/**
 * A CSS colour plus the spec's separate 0–1 pill opacity, as one rgba() string.
 *
 * The colour is not always the `#rrggbb` the editor's colour input produces —
 * the AI layout pass writes `#fff` and `rgb()`/`rgba()` too. Returning those
 * untouched silently discarded the opacity, which is how opening and saving a
 * translucent pill turned it opaque. Anything this cannot parse (a named
 * colour, `hsl()`) keeps its colour and says so loudly rather than quietly
 * rendering solid.
 */
export function colorWithOpacity(color: string, opacity: number): string {
  const alpha = Number.isFinite(opacity) ? Math.max(0, Math.min(1, opacity)) : 1;
  const raw = (color || '').trim();

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(raw);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].replace(/./g, c => c + c) : hex[1];
    const n = parseInt(digits, 16);
    // eslint-disable-next-line no-bitwise
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }

  // Both the legacy `rgb(r, g, b)` / `rgba(r, g, b, a)` and the modern
  // `rgb(r g b / a)` spelling. An alpha already on the colour multiplies with
  // the spec's, so neither one is thrown away.
  const rgb = /^rgba?\(([^)]*)\)$/i.exec(raw);
  if (rgb) {
    const parts = rgb[1].split(/[\s,/]+/).filter(Boolean);
    if (parts.length === 3 || parts.length === 4) {
      const own = parts.length === 4
        ? (parts[3].endsWith('%') ? Number(parts[3].slice(0, -1)) / 100 : Number(parts[3]))
        : 1;
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${(Number.isFinite(own) ? own : 1) * alpha})`;
    }
  }

  if (alpha < 1) {
    console.warn(
      `Overlay pill colour "${raw}" is not a hex or rgb() value — its ${alpha} opacity cannot be `
      + 'applied, so it renders fully opaque.',
    );
  }
  return raw;
}

export interface GradientBox { left: number; top: number; width: number; height: number }

/**
 * The server's gradient (textOverlay.js gradientCoords) uses SVG
 * objectBoundingBox units: (0.5 ± cos/2, 0.5 ± sin/2) scaled PER AXIS by the
 * text block's own bbox — on a non-square block a 45° gradient runs along the
 * box diagonal, not at 45° in pixels. Same mapping here, in canvas px.
 */
export function gradientEndpoints(box: GradientBox, angleDeg: number | undefined): { x1: number; y1: number; x2: number; y2: number } {
  const rad = (((Number(angleDeg) || 0) % 360) * Math.PI) / 180;
  const dx = Math.cos(rad) / 2;
  const dy = Math.sin(rad) / 2;
  return {
    x1: box.left + (0.5 - dx) * box.width,
    y1: box.top + (0.5 - dy) * box.height,
    x2: box.left + (0.5 + dx) * box.width,
    y2: box.top + (0.5 + dy) * box.height,
  };
}

/**
 * The CSS `linear-gradient()` angle that reproduces the same direction on a
 * DOM block of the given aspect ratio (CSS angles: 0deg = bottom→top,
 * clockwise). Axis-aligned spec angles are aspect-independent; diagonals must
 * be corrected by the box shape to match the objectBoundingBox stretch.
 */
export function gradientCssAngle(angleDeg: number | undefined, boxWidth: number, boxHeight: number): number {
  const rad = (((Number(angleDeg) || 0) % 360) * Math.PI) / 180;
  return 90 + (Math.atan2(Math.sin(rad) * boxHeight, Math.cos(rad) * boxWidth) * 180) / Math.PI;
}

function fontString(el: OverlayElement, seg: { bold: boolean; italic: boolean }, stack: string): string {
  const weight = seg.bold
    ? Math.min(900, Math.max(700, (el.fontWeight || 400) + 100))
    : (el.fontWeight || 400);
  const italic = seg.italic || el.fontStyle === 'italic' ? 'italic ' : '';
  return `${italic}${weight} ${el.fontSize}px ${stack}`;
}

/**
 * Where the glyphs actually sit relative to the baseline, in ems.
 *
 * MUST match inkExtentEm in the collector's statics/textOverlay.js — that draws
 * the published pill, this draws the one the operator approves, and a difference
 * means the preview is lying.
 */
const ASCENDER_RE = /[A-Z0-9bdfhklt!?#$&@(){}[\]/\\|"']/;
const DESCENDER_RE = /[gjpqyQJ,;$(){}[\]/\\|]/;

export function inkExtentEm(lines: string[]): { ascent: number; descent: number } {
  // Markup markers are not rendered, so they must not vote on the extent.
  const text = (lines || []).join('').replace(/\*/g, '');
  return {
    ascent: ASCENDER_RE.test(text) ? 0.75 : 0.53,
    descent: DESCENDER_RE.test(text) ? 0.21 : 0.03,
  };
}

/** Widths per line via real canvas measurement — the anchor math's ground truth.
 * Must filter blanks exactly like drawOverlay, which indexes these widths by
 * the filtered line position. */
function measureLines(ctx: CanvasRenderingContext2D, el: OverlayElement, stack: string): number[] {
  return renderableLines(el.lines).map((line) => {
    let width = 0;
    for (const seg of parseInlineMarkup(line)) {
      ctx.font = fontString(el, seg, stack);
      width += ctx.measureText(seg.text).width;
    }
    return width;
  });
}

export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  elements: OverlayElement[],
  fontStackFor: (family?: string) => string,
): void {
  for (const el of elements) {
    const lines = renderableLines(el.lines);
    if (!lines.length) continue;
    const fontSize = el.fontSize || 40;
    const lineHeight = (el.lineHeight || 1.2) * fontSize;
    const anchor = el.align === 'middle' || el.align === 'end' ? el.align : 'start';
    const x = el.x || 0;
    const y = el.y || fontSize;
    const stack = fontStackFor(el.fontFamily);

    const widths = measureLines(ctx, el, stack);
    const maxWidth = Math.max(...widths, 0);

    // Backing pill — padding around the type's real INK, not its em box.
    // See inkExtentEm: an all-caps line has no descender, so an em-box pill left
    // more room under the letters than over them and the text rode high.
    if (el.background?.color) {
      const pad = el.background.padding ?? 12;
      const bgX = anchor === 'middle' ? x - maxWidth / 2 : anchor === 'end' ? x - maxWidth : x;
      const { ascent, descent } = inkExtentEm(lines);
      const top = y - fontSize * ascent - pad;
      const height = lineHeight * (lines.length - 1) + fontSize * (ascent + descent) + pad * 2;
      ctx.beginPath();
      const radius = el.background.radius ?? 0;
      ctx.roundRect(bgX - pad, top, maxWidth + pad * 2, height, radius);
      ctx.fillStyle = colorWithOpacity(el.background.color, el.background.opacity ?? 1);
      ctx.fill();
    }

    // Fill: solid color, or a linear gradient across the block's bbox with
    // the same angle semantics as the server (0° = left→right, 90° = top→bottom).
    let fill: string | CanvasGradient = el.color || '#ffffff';
    if (el.gradient?.from && el.gradient?.to) {
      const left = anchor === 'middle' ? x - maxWidth / 2 : anchor === 'end' ? x - maxWidth : x;
      const top = y - fontSize * 0.78;
      const bottom = y + lineHeight * (lines.length - 1) + fontSize * 0.22;
      const { x1, y1, x2, y2 } = gradientEndpoints(
        { left, top, width: maxWidth, height: bottom - top },
        el.gradient.angle ?? 90,
      );
      const grad = ctx.createLinearGradient(x1, y1, x2, y2);
      grad.addColorStop(0, el.gradient.from);
      grad.addColorStop(1, el.gradient.to);
      fill = grad;
    }

    lines.forEach((line, i) => {
      const baseline = y + i * lineHeight;
      const lineWidth = widths[i];
      let cursor = anchor === 'middle' ? x - lineWidth / 2 : anchor === 'end' ? x - lineWidth : x;
      for (const seg of parseInlineMarkup(line)) {
        ctx.font = fontString(el, seg, stack);
        ctx.fillStyle = fill;
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(seg.text, cursor, baseline);
        cursor += ctx.measureText(seg.text).width;
      }
    });
  }
}

/** Full composite: plate + overlay → JPEG blob at plate resolution. */
export async function renderOverlayToBlob(
  plate: ImageBitmap,
  elements: OverlayElement[],
  fontStackFor: (family?: string) => string,
): Promise<Blob> {
  await (document as any).fonts?.ready;
  const canvas = document.createElement('canvas');
  canvas.width = plate.width;
  canvas.height = plate.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D unavailable');
  ctx.drawImage(plate, 0, 0);
  drawOverlay(ctx, elements, fontStackFor);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      0.92,
    );
  });
}
